import Head from 'expo-router/head';
import MapPin from 'lucide-react-native/icons/map-pin';
import { useState } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { listSeedScript } from '@/components/detail/list-seed';
import { BoatCard } from '@/components/riviera/boat-card';
import {
  BoatGroup,
  CityChip,
  MoreLink,
  SectionHeading,
  capitalize,
  eur,
} from '@/components/riviera/boat-rental-screen';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { Fonts, MaxContentWidth, Spacing } from '@/constants/theme';
import { useBoats } from '@todo/core/hooks/use-boats';
import { useTheme } from '@/hooks/use-theme';
import {
  BOAT_TYPE_PAGES,
  MIN_BOATS_PER_TYPE_PAGE,
  TOP_BOATS_ON_CITY_PAGE,
  boatCityStats,
  boatPriceBands,
  boatTypeKey,
  boatTypePages,
  boatsOfTypePage,
  isBoatTypePage,
  type BoatCityStats,
  type BoatPriceBand,
  type BoatTypePage,
  type BoatTypePages,
} from '@/lib/boat-rental';
import type { Boat } from '@todo/core/boats';
import { cities } from '@/lib/events-data';
import { localizedBoatTitle } from '@/lib/i18n/boat-content';
import { boatRentalCityCopy } from '@/lib/i18n/boat-rental-content';
import type { Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import {
  BOAT_TYPE_PAGES_SEED_KEY,
  LIST_BUCKET,
  boatRentalCityQuery,
  boatTypePageSeedKey,
  boatTypeStatsSeedKey,
} from '@todo/core/list-queries';
import {
  SITE_NAME,
  absoluteUrl,
  boatsCrumb,
  breadcrumbJsonLd,
  faqPageJsonLd,
  hreflangLinks,
  itemListJsonLd,
  richResultImage,
  seoTitle,
  truncate,
} from '@todo/core/seo';
import { getPrefetched } from '@todo/core/static-prefetch';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** "Under €350" / "€350–700" / "€1500 and up" — the hub's price filter labels. */
function bandLabel(band: BoatPriceBand, t: Translate): string {
  if (band.min == null && band.max != null) return t('hubFilters.priceUnder', { price: eur(band.max) });
  if (band.min != null && band.max == null) return t('hubFilters.priceOver', { price: eur(band.min) });
  return t('hubFilters.priceBetween', { min: eur(band.min ?? 0), max: eur(band.max ?? 0) });
}

/**
 * The type page's FAQ — like the city page's, its answers come from this
 * city × type's own numbers (prices, price spread, ports), plus the licence
 * rules for this kind of boat.
 */
function typeFaq(
  type: BoatTypePage,
  stats: BoatCityStats,
  bands: BoatPriceBand[],
  vars: { inCity: string; plural: string; pluralCap: string },
  locale: Locale,
  t: Translate,
) {
  if (stats.total === 0) return [];
  const sep = t('boatRental.listSeparator');
  const faq: { q: string; a: string }[] = [];

  if (stats.minPrice != null && stats.medianPrice != null) {
    faq.push({
      q: t(`boatRental.typePages.${type}.priceQ`, vars),
      a: t('boatRental.typePriceA', {
        ...vars,
        min: eur(stats.minPrice),
        median: eur(stats.medianPrice),
        bands: bands
          .map((band) => {
            const label = bandLabel(band, t);
            const lower = label.charAt(0).toLocaleLowerCase(locale) + label.slice(1);
            return `${lower} — ${t('hubFilters.boatsCount', { count: band.count })}`;
          })
          .join(sep),
      }),
    });
  }

  faq.push({
    q: t(`boatRental.typePages.${type}.licenceQ`, vars),
    a: t(`boatRental.typePages.${type}.licenceA`, vars),
  });

  if (stats.ports.length > 0) {
    faq.push({
      q: t('boatRental.typePortsQ', vars),
      a: t('boatRental.typePortsA', {
        ...vars,
        list: stats.ports.map((port) => `${port.name} (${port.count})`).join(sep),
      }),
    });
  }

  return faq;
}

/**
 * The /[lang]/boat-rental/[city]/[type] landing page — one boat type in one
 * city (catamarans in Cannes, licence-free boats in Antibes…). Search Console
 * showed those queries ("location catamaran cannes", "bateau sans permis
 * antibes") landing on single boat pages at position 60–80, because nothing
 * on the site matched them: this page answers each with that city × type's
 * own boats, prices, price spread and departure ports.
 */
export function BoatTypeScreen({ cityId, type: typeParam }: { cityId: string; type: string }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const city = cities.find((c) => c.id === cityId);
  const copy = city ? boatRentalCityCopy(locale, city.id) : undefined;
  const type = isBoatTypePage(typeParam) ? typeParam : undefined;
  const valid = city != null && copy != null && type != null;

  // The seed holds just this type's boats; without one (native, or a
  // client-side navigation) the whole city is fetched and filtered here.
  const { boats: rows, loading, error } = useBoats(
    boatRentalCityQuery(cityId),
    valid ? boatTypePageSeedKey(city.id, type, locale) : undefined,
  );
  const boats = type ? boatsOfTypePage(rows, type) : [];

  const [bakedStats] = useState(() =>
    valid ? getPrefetched<BoatCityStats>(LIST_BUCKET, boatTypeStatsSeedKey(city.id, type)) : undefined,
  );
  const stats = bakedStats ?? boatCityStats(boats);

  // Which city × type pages exist — the exact list the export generated. On
  // native nothing is baked: derive this city's from its rows (other cities
  // aren't loaded, so they get no chips there).
  const [bakedPages] = useState(() =>
    getPrefetched<BoatTypePages>(LIST_BUCKET, BOAT_TYPE_PAGES_SEED_KEY),
  );
  const pages: BoatTypePages = bakedPages ?? boatTypePages(rows);

  if (!valid) {
    return (
      <View style={[styles.root, { backgroundColor: theme.background }]}>
        <Head key={locale}>
          <title>{`${t('common.notFound')} — ${SITE_NAME}`}</title>
          <meta name="robots" content="noindex, follow" />
        </Head>
        <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
          <SiteHeader />
        </SafeAreaView>
        <Text style={[styles.status, { color: theme.mutedForeground, paddingHorizontal: Spacing.three }]}>
          {t('common.notFound')}
        </Text>
      </View>
    );
  }

  const inCity = copy.inCity;
  const plural = t(`boatRental.typePages.${type}.plural`);
  const vars = { inCity, plural, pluralCap: capitalize(plural, locale) };
  const heading = t(`boatRental.typePages.${type}.title`, vars);
  const path = `boat-rental/${city.id}/${type}`;
  const canonical = absoluteUrl(locale, path);
  const title = seoTitle(t(`boatRental.typePages.${type}.seoTitle`, vars));
  const countText =
    stats.total > 0 ? t(`boatRental.typePages.${type}.count`, { count: stats.total }) : '';
  const metaDescription = truncate(
    stats.total > 0 && stats.minPrice != null && stats.medianPrice != null
      ? t('boatRental.typeMeta', {
          ...vars,
          countText,
          price: eur(stats.minPrice),
          median: eur(stats.medianPrice),
        })
      : heading,
  );
  const ogImage = richResultImage(boats[0]?.image_url);
  // Too few boats to be worth indexing — see MIN_BOATS_PER_TYPE_PAGE. Only
  // once loaded, so a page still fetching client-side isn't flagged.
  const thin = !loading && boats.length < MIN_BOATS_PER_TYPE_PAGE;
  const bands = boatPriceBands(boats);
  const faq = typeFaq(type, stats, bands, vars, locale, t);
  const top = boats.slice(0, TOP_BOATS_ON_CITY_PAGE);
  const siblingTypes = (pages[city.id] ?? []).filter((other) => other !== type);
  const otherCities = cities.filter((c) => c.id !== city.id && pages[c.id]?.includes(type));

  // Every boat, grouped by raw type (a yacht page splits motor and sailing
  // yachts), most boats first — as on the city page.
  const groups = new Map<string, Boat[]>();
  for (const boat of boats) {
    const key = boatTypeKey(boat);
    const list = groups.get(key);
    if (list) list.push(boat);
    else groups.set(key, [boat]);
  }
  const orderedGroups = [...groups.entries()].sort(
    (a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]),
  );

  const statsLine =
    stats.total > 0 && stats.minPrice != null && stats.medianPrice != null
      ? [
          t('boatRental.typeStats', {
            countText,
            price: eur(stats.minPrice),
            median: eur(stats.medianPrice),
          }),
          stats.avgRating != null ? t('boatRental.ratingStat', { rating: stats.avgRating }) : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : null;

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      {/* key forces a fresh mount on locale change — see the comment in
          app/[lang]/_layout.tsx for why. */}
      <Head key={locale}>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        {thin ? <meta name="robots" content="noindex, follow" /> : null}
        <link rel="canonical" href={canonical} />
        {hreflangLinks(path).map((link) => (
          <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
        ))}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonical} />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={metaDescription} />
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
        <script type="application/ld+json">
          {JSON.stringify(
            breadcrumbJsonLd(locale, [
              boatsCrumb(locale),
              { name: copy.title, path: `boat-rental/${city.id}` },
              { name: heading },
            ]),
          )}
        </script>
        {top.length > 0 ? (
          <script type="application/ld+json">
            {JSON.stringify(
              itemListJsonLd(
                top.map((boat) => ({
                  name: localizedBoatTitle(boat, locale),
                  url: absoluteUrl(locale, `boats/${boat.id}`),
                })),
                canonical,
              ),
            )}
          </script>
        ) : null}
        {faq.length > 0 ? (
          <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(faq))}</script>
        ) : null}
        {/* Baked rows + stats + page list so the client's first render matches this static HTML. */}
        {listSeedScript(boatTypePageSeedKey(city.id, type, locale))}
        {listSeedScript(boatTypeStatsSeedKey(city.id, type))}
        {listSeedScript(BOAT_TYPE_PAGES_SEED_KEY)}
      </Head>
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView style={{ flex: 1 }}>
        <View style={styles.centered}>
          <View style={styles.page}>
            {/* role="heading" + aria-level renders a real <h1> on web (via
                react-native-web) while staying valid on native. */}
            <Text
              role="heading"
              aria-level={1}
              style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {heading}
            </Text>
            {statsLine ? <Text style={[styles.stats, { color: theme.gold }]}>{statsLine}</Text> : null}
            <Text style={[styles.intro, { color: theme.text }]}>
              {t(`boatRental.typePages.${type}.intro`, vars)}
            </Text>

            <View style={styles.chipsBox}>
              <Text style={[styles.smallHeading, { color: theme.mutedForeground }]}>
                {t('boatRental.typeOtherTypesHeading', vars)}
              </Text>
              <View style={styles.chipsRow}>
                <CityChip
                  pin={false}
                  active={false}
                  label={t('hubFilters.boatTypeOptions.any')}
                  href={{ pathname: '/[lang]/boat-rental/[city]', params: { lang: locale, city: city.id } }}
                />
                {siblingTypes.map((other) => (
                  <CityChip
                    key={other}
                    pin={false}
                    active={false}
                    label={t(`hubFilters.boatTypeOptions.${BOAT_TYPE_PAGES[other]}`)}
                    href={{
                      pathname: '/[lang]/boat-rental/[city]/[type]',
                      params: { lang: locale, city: city.id, type: other },
                    }}
                  />
                ))}
              </View>
            </View>

            {otherCities.length > 0 ? (
              <View style={styles.chipsBox}>
                <Text style={[styles.smallHeading, { color: theme.mutedForeground }]}>
                  {t('boatRental.typeOtherCitiesHeading', vars)}
                </Text>
                <View style={styles.chipsRow}>
                  {otherCities.map((other) => (
                    <CityChip
                      key={other.id}
                      active={false}
                      label={other.name}
                      href={{
                        pathname: '/[lang]/boat-rental/[city]/[type]',
                        params: { lang: locale, city: other.id, type },
                      }}
                    />
                  ))}
                </View>
              </View>
            ) : null}

            {loading ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('boatRental.loading')}
              </Text>
            ) : error ? (
              <Text style={[styles.status, { color: theme.destructive }]}>{error}</Text>
            ) : boats.length === 0 ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('boatRental.empty')}
              </Text>
            ) : (
              <>
                <View style={styles.section}>
                  <SectionHeading>{t('boatRental.typeTopHeading', vars)}</SectionHeading>
                  <View style={styles.grid}>
                    {/* First row is above the fold on desktop — eager, as on /boats. */}
                    {top.map((boat, index) => (
                      <BoatCard key={boat.id} boat={boat} eager={index < 4} />
                    ))}
                  </View>
                </View>

                {bands.length > 0 ? (
                  <View style={styles.section}>
                    <SectionHeading>{t('boatRental.typePricesHeading')}</SectionHeading>
                    {bands.map((band) => (
                      <View
                        key={`${band.min ?? ''}-${band.max ?? ''}`}
                        style={[styles.priceRow, { borderBottomColor: theme.border }]}>
                        <Text style={[styles.priceName, { color: theme.text }]}>{bandLabel(band, t)}</Text>
                        <Text style={[styles.priceFacts, { color: theme.textSecondary }]}>
                          {t('hubFilters.boatsCount', { count: band.count })}
                        </Text>
                      </View>
                    ))}
                  </View>
                ) : null}

                {stats.ports.length > 0 ? (
                  <View style={styles.section}>
                    <SectionHeading>{t('boatRental.portsHeading')}</SectionHeading>
                    <View style={styles.chipsRow}>
                      {stats.ports.map((port) => (
                        <View
                          key={port.name}
                          style={[styles.chip, { backgroundColor: theme.card, borderColor: theme.border }]}>
                          <MapPin size={12} color={theme.mutedForeground} />
                          <Text style={[styles.chipText, { color: theme.text }]}>
                            {`${port.name} · ${port.count}`}
                          </Text>
                        </View>
                      ))}
                    </View>
                  </View>
                ) : null}

                <View style={styles.section}>
                  <SectionHeading>{t('boatRental.typeAllHeading', vars)}</SectionHeading>
                  {orderedGroups.map(([rawType, list]) => (
                    <BoatGroup key={rawType} type={rawType} rows={list} locale={locale} shortNames />
                  ))}
                </View>
              </>
            )}

            {faq.length > 0 ? (
              <View style={styles.section}>
                <SectionHeading>{t('boatRental.typeFaqHeading', { title: heading })}</SectionHeading>
                {faq.map((entry) => (
                  <View key={entry.q} style={styles.faqItem}>
                    <Text style={[styles.faqQuestion, { color: theme.text }]}>{entry.q}</Text>
                    <Text style={[styles.faqAnswer, { color: theme.textSecondary }]}>{entry.a}</Text>
                  </View>
                ))}
              </View>
            ) : null}

            <View style={styles.moreLinks}>
              <MoreLink
                label={copy.title}
                href={{ pathname: '/[lang]/boat-rental/[city]', params: { lang: locale, city: city.id } }}
              />
              <MoreLink
                label={t('boatRental.allBoats')}
                href={{ pathname: '/[lang]/boats', params: { lang: locale } }}
              />
            </View>
          </View>
        </View>
        <SiteFooter />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  centered: {
    alignItems: 'center',
    width: '100%',
  },
  page: {
    width: '100%',
    maxWidth: MaxContentWidth,
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.four,
    gap: Spacing.three,
  },
  heading: {
    fontSize: 26,
    fontWeight: '700',
  },
  stats: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: -Spacing.two,
  },
  intro: {
    fontSize: 15,
    lineHeight: 23,
  },
  chipsBox: {
    gap: Spacing.two,
  },
  smallHeading: {
    fontSize: 13,
    fontWeight: '600',
  },
  chipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.one + 2,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Spacing.two,
    paddingVertical: 6,
    borderWidth: 1,
    borderRadius: 999,
  },
  chipText: {
    fontSize: 13,
    fontWeight: '500',
  },
  section: {
    gap: Spacing.two,
    marginTop: Spacing.two,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.three,
    justifyContent: 'center',
  },
  priceRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: Spacing.two,
    paddingVertical: Spacing.two,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  priceName: {
    fontSize: 15,
    fontWeight: '600',
  },
  priceFacts: {
    fontSize: 14,
  },
  faqItem: {
    gap: 4,
  },
  faqQuestion: {
    fontSize: 14,
    fontWeight: '700',
  },
  faqAnswer: {
    fontSize: 14,
    lineHeight: 21,
  },
  moreLinks: {
    marginTop: Spacing.two,
    gap: Spacing.two,
  },
  status: {
    fontSize: 14,
    paddingVertical: Spacing.four,
  },
});
